import type { QueryClient } from "@tanstack/react-query";
import type { TRPCLink } from "@trpc/client";
import type { AnyRouter } from "@trpc/server";
import type { EventSourceFetchInit } from "eventsource";

import * as React from "react";

import {
  httpLink,
  httpSubscriptionLink,
  loggerLink,
  splitLink,
} from "@trpc/client";
import {
  createTRPCClient,
  createTRPCQueryUtils,
  createTRPCReact,
} from "@trpc/react-query";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import { getTokenFromStorage } from "@v2/features/infra/auth";
import { FatalAwareEventSource } from "@v2/trpc/FatalAwareEventSource";
import { defaultFetchCredentials, getPublisherUrl } from "@v2/utils/env";
import superjson from "superjson";

const transformer = superjson;

export function createTrpcRouterConfig<TRouter extends AnyRouter>(args: {
  path: string;
  queryClient: QueryClient;
}) {
  const { path, queryClient } = args;

  const router = createTRPCReact<TRouter>({
    context: React.createContext(null),
  });

  const baseHttpUrl = `${getPublisherUrl()}/api/v1/trpc${path ? `/${path}` : ""}`;

  const http = httpLink({
    url: baseHttpUrl,
    headers: () => ({
      "Cache-Control": "no-cache",
      Authorization: `Token ${getTokenFromStorage()}`,
    }),
    transformer,
    fetch(url, options) {
      return fetch(url, {
        ...options,
        credentials: defaultFetchCredentials,
      });
    },
  });

  const subscriptionLink = httpSubscriptionLink({
    url: baseHttpUrl,
    transformer,
    EventSource: FatalAwareEventSource,
    eventSourceOptions: () => ({
      fetch: (input: string | URL, init: EventSourceFetchInit) => {
        return fetch(input, {
          ...init,
          credentials: defaultFetchCredentials,
          headers: {
            ...init.headers,
            Authorization: `Token ${getTokenFromStorage()}`,
          },
        });
      },
    }),
  });

  const finalLinks: TRPCLink<TRouter>[] = [
    loggerLink(),
    splitLink({
      condition: (op) => op.type === "subscription",
      true: subscriptionLink,
      false: http,
    }),
  ];

  const client = createTRPCClient<TRouter>({ links: finalLinks });

  const options = createTRPCOptionsProxy<TRouter>({
    client: client,
    queryClient,
  });

  const utils = createTRPCQueryUtils({
    queryClient,
    client,
  });

  return { router, client, utils, options } as const;
}
